import { useEffect, useState, useRef } from "react";
import { Dialog } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import "./UploadFileDialog.css";
import CustomInput from "../textField/scmInput/CustomInput";
import { useTranslation } from "react-i18next";

export default function UploadFileDialog({
    open,
    onClose,
    initialPath,
    onUpload,
    accept,
}) {
    const { t } = useTranslation();
    const [uploadPath, setUploadPath] = useState("");
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setUploadPath(initialPath || "");
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }, [open, initialPath]);

    const canUpload = uploadPath.trim().length > 0 && !!file;

    const handleFileChange = (e) => {
        const f = e.target.files?.[0] || null;
        setFile(f);
    };

    const handleRemoveFile = () => {
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            classes={{ paper: "upload-custom-dialog" }}
            maxWidth="sm"
            fullWidth
        >
            <div className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">{t("Upload File")}</h6>
                    <button className="btn btn-sm" onClick={onClose}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <CustomInput
                    label={t('Upload Path')}
                    value={uploadPath}
                    onChange={(e) => setUploadPath(e.target.value)}
                    placeholder={t('Enter or modify upload path')}
                    allowedChars={/^[A-Za-z0-9_.\/-]*$/}
                />

                <label className="form-label">Select File</label>
                <input
                    ref={fileInputRef}
                    type="file"
                    className="form-control"
                    onChange={handleFileChange}
                    accept={accept}
                />

                {file && (
                    <div className="mt-2 small text-muted d-flex align-items-center gap-2">
                        <span title={file.name}>
                            {file.name} ({file.size.toLocaleString()} bytes)
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary ms-auto"
                            onClick={handleRemoveFile}
                        >
                            {t("Remove")}
                        </button>
                    </div>
                )}

                <div className="d-flex justify-content-end gap-2 mt-4">
                    <button className="btn btn-outline-secondary" onClick={onClose} type="button">
                        {t("Cancel")}
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            if (!canUpload) return;
                            const fullPath = `${uploadPath.trim().replace(/\/?$/, "/")}${file.name}`;
                            onUpload?.({ path: fullPath, file });
                            onClose();
                        }}
                        disabled={!canUpload}
                        type="button"
                    >
                        {t("Upload")}
                    </button>
                </div>
            </div>
        </Dialog>
    );
}
